import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import {
  SitesCircleGeofenceDatum,
  SitesPolygonGeofenceDatum,
} from '../hooks/useSitesMapData';

/**
 * Vertical offset from cursor position (pixels).
 * Positions tooltip above the cursor.
 */
const TOOLTIP_OFFSET_Y = 10;

interface GeofenceHoverTooltipProps {
  /** The geofence data to display */
  geofence: SitesCircleGeofenceDatum | SitesPolygonGeofenceDatum;
  /** Whether this is a circle geofence */
  isCircle: boolean;
  /** Screen position from DeckGL picking */
  position: { x: number; y: number };
}

/**
 * Formats a geofence tag to a human-readable label.
 */
function formatGeofenceTag(tag: string | undefined): string {
  if (!tag) {
    return t('administration.site.geofence.additional', 'Additional');
  }

  switch (tag.toLowerCase()) {
    case 'loading':
      return t('administration.site.geofence.loading', 'Loading');
    case 'unloading':
      return t('administration.site.geofence.unloading', 'Unloading');
    case 'check_in':
      return t('administration.site.geofence.check_in', 'Check In');
    case 'scaling':
      return t('administration.site.geofence.scaling', 'Scaling');
    case 'ticketing':
      return t('administration.site.geofence.ticketing', 'Ticketing');
    default:
      // Capitalize first letter of each word
      return tag
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
}

/**
 * Formats a radius in meters to a human-readable string.
 */
function formatRadius(radiusMeters: number): string {
  if (radiusMeters >= 1000) {
    const km = radiusMeters / 1000;
    return `${km.toFixed(km >= 10 ? 0 : 1)}km`;
  }
  return `${Math.round(radiusMeters)}m`;
}

/**
 * GeofenceHoverTooltip displays geofence information when hovering over a geofence.
 * Shows the site name, geofence type, and shape/size.
 */
export function GeofenceHoverTooltip({
  geofence,
  isCircle,
  position,
}: GeofenceHoverTooltipProps) {
  const theme = useTheme();

  const typeLabel = geofence.isPrimary
    ? t('administration.site.geofence.primary', 'Primary Geofence')
    : formatGeofenceTag(geofence.tag);

  const shapeLabel = isCircle
    ? `Circle - ${formatRadius((geofence as SitesCircleGeofenceDatum).radiusMeters)}`
    : 'Polygon';

  return (
    <Box
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y - TOOLTIP_OFFSET_Y,
        transform: 'translate(-50%, -100%)',
        zIndex: 1500,
        pointerEvents: 'none',
        bgcolor: 'background.paper',
        borderRadius: theme.brandV2.borderRadius,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        px: 1.5,
        py: 1,
        maxWidth: 280,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontSize: '13px',
          color: theme.brandV2.colors.treadBlack,
          wordBreak: 'break-word',
          lineHeight: 1.3,
        }}
      >
        {geofence.siteName}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: theme.brandV2.colors.treadGray3,
          mt: 0.25,
        }}
      >
        {typeLabel} · {shapeLabel}
      </Typography>
    </Box>
  );
}
